import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';

const APPLICATIONS_DATA = [
  { id: 1, name: 'Esther Howard', role: 'UI/UX Designer', applied: 'Oct 24, 2023', status: 'Reviewing', score: '85%' },
  { id: 2, name: 'Cameron Williamson', role: 'Full Stack Developer', applied: 'Oct 22, 2023', status: 'Shortlisted', score: '92%' },
  { id: 3, name: 'Brooklyn Simmons', role: 'Marketing Manager', applied: 'Oct 20, 2023', status: 'Rejected', score: '45%' },
];

const StatusBadge = ({ status }) => {
  const styles = {
    'Reviewing': 'bg-blue-50 text-blue-600',
    'Shortlisted': 'bg-[#11469A]/10 text-[#11469A]',
    'Rejected': 'bg-red-50 text-red-600',
  };
  return (
    <span className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider ${styles[status]}`}>
      {status}
    </span>
  );
};

const Applications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');

  const filteredApps = APPLICATIONS_DATA.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) || app.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Applications</h1>
          <p className="text-gray-500">Review and manage candidate submissions.</p>
        </div>
      </div>

      <div className="card p-4 flex flex-wrap gap-4 items-center border border-gray-100 shadow-sm">
        <div className="relative flex-1 min-w-[280px]">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
            <SafeIcon name="Search" size={18} />
          </div>
          <input 
            type="text" 
            placeholder="Search applications..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] focus:ring-1 focus:ring-[#11469A]/20 transition-all outline-none bg-white" 
          />
        </div>
        <div className="flex gap-2">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full min-w-[140px] px-4 pr-10 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] transition-all outline-none bg-white text-gray-600 text-sm font-medium appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px_12px] bg-[right_16px_center] bg-no-repeat"
          >
            <option>All Status</option>
            <option>Reviewing</option>
            <option>Shortlisted</option>
            <option>Rejected</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden border border-gray-100 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50/50 text-gray-400 font-semibold uppercase text-[11px] tracking-widest border-b border-gray-100">
              <tr>
                <th className="px-6 py-4">CANDIDATE</th>
                <th className="px-6 py-4">APPLIED ROLE</th>
                <th className="px-6 py-4">DATE APPLIED</th>
                <th className="px-6 py-4">MATCH SCORE</th>
                <th className="px-6 py-4">STATUS</th>
                <th className="px-6 py-4 text-right">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredApps.length > 0 ? (
                filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-800">{app.name}</div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-600">{app.role}</td>
                    <td className="px-6 py-4 text-gray-500 font-medium">{app.applied}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-full max-w-[120px] h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#11469A] rounded-full" style={{ width: app.score }}></div>
                        </div>
                        <span className="text-xs font-bold text-gray-700">{app.score}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={app.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="px-3 py-1.5 text-[#11469A] font-bold text-xs hover:bg-[#11469A]/10 rounded-lg transition-all">
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No applications match your filtering criteria.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Applications;